export type ConsoleLogLevel = 'log' | 'info' | 'warn' | 'error';

export interface ConsoleLogEntry {
  t: string;
  level: ConsoleLogLevel;
  message: string;
}

export const MAX_LOG_ENTRIES = 150;
export const MAX_MESSAGE_CHARS = 500;
export const MAX_LOGS_PAYLOAD_BYTES = 80 * 1024;

const BEARER_RE = /Bearer\s+[A-Za-z0-9._\-+/=]+/gi;
const SECRET_ASSIGN_RE =
  /((?:api[_-]?key|access[_-]?token|id[_-]?token|refresh[_-]?token|secret|password|auth[_-]?token))\s*=\s*([^\s&]+)/gi;
const JWT_RE = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9._-]+/g;

let installed = false;
const buffer: ConsoleLogEntry[] = [];
const originals: Partial<Record<ConsoleLogLevel, (...args: unknown[]) => void>> = {};

export function redactSecrets(text: string): string {
  return text
    .replace(BEARER_RE, 'Bearer [redacted]')
    .replace(SECRET_ASSIGN_RE, '$1=[redacted]')
    .replace(JWT_RE, '[redacted]');
}

export function serializeConsoleArg(arg: unknown): string {
  if (arg instanceof Error) {
    const stackLine = arg.stack?.split('\n')[1]?.trim();
    return stackLine ? `${arg.name}: ${arg.message} ${stackLine}` : `${arg.name}: ${arg.message}`;
  }
  if (typeof arg === 'string') return arg;
  if (typeof arg === 'number' || typeof arg === 'boolean' || arg == null) return String(arg);
  try {
    return JSON.stringify(arg);
  } catch {
    return Object.prototype.toString.call(arg);
  }
}

export function formatConsoleArgs(args: unknown[]): string {
  return args.map(serializeConsoleArg).join(' ');
}

export function capMessage(message: string): string {
  const redacted = redactSecrets(message);
  if (redacted.length <= MAX_MESSAGE_CHARS) return redacted;
  return `${redacted.slice(0, MAX_MESSAGE_CHARS - 1)}…`;
}

function payloadBytes(entries: ConsoleLogEntry[]): number {
  const json = JSON.stringify(entries);
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(json).length;
  }
  return unescape(encodeURIComponent(json)).length;
}

export function capLogEntries(entries: ConsoleLogEntry[]): ConsoleLogEntry[] {
  let next = entries.slice(-MAX_LOG_ENTRIES).map(entry => ({
    ...entry,
    message: capMessage(entry.message),
  }));
  while (next.length > 1 && payloadBytes(next) > MAX_LOGS_PAYLOAD_BYTES) {
    next = next.slice(Math.ceil(next.length * 0.15) || 1);
  }
  if (payloadBytes(next) > MAX_LOGS_PAYLOAD_BYTES && next.length === 1) {
    const only = next[0];
    let msg = only.message;
    while (msg.length > 32 && payloadBytes([{ ...only, message: msg }]) > MAX_LOGS_PAYLOAD_BYTES) {
      msg = `${msg.slice(0, Math.floor(msg.length * 0.7))}…`;
    }
    return [{ ...only, message: msg }];
  }
  return next;
}

function pushEntry(level: ConsoleLogLevel, args: unknown[]): void {
  buffer.push({
    t: new Date().toISOString(),
    level,
    message: capMessage(formatConsoleArgs(args)),
  });
  if (buffer.length > MAX_LOG_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_LOG_ENTRIES);
  }
}

export function snapshotConsoleLogs(): ConsoleLogEntry[] {
  return capLogEntries(buffer);
}

export function resetConsoleLogBufferForTests(): void {
  buffer.length = 0;
}

export function installConsoleLogBuffer(): void {
  if (installed || typeof console === 'undefined') return;
  installed = true;

  (['log', 'info', 'warn', 'error'] as const).forEach(level => {
    const original = console[level].bind(console);
    originals[level] = original;
    console[level] = (...args: unknown[]) => {
      try {
        pushEntry(level, args);
      } catch {
        /* never break console */
      }
      original(...args);
    };
  });

  if (typeof window === 'undefined') return;

  window.addEventListener('error', event => {
    const message = event.message || (event.error as Error | undefined)?.message || 'window.error';
    pushEntry('error', [message]);
  });

  window.addEventListener('unhandledrejection', event => {
    pushEntry('error', ['unhandledrejection', event.reason]);
  });
}
